import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useAnimation, useReducedMotion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sprites are proxied via backend (Wikia blocks browser hotlinks via Referer).
const SPRITES = Array.from({ length: 7 }, (_, i) => `${BACKEND_URL}/api/sprite/${i}`);

// Spread the sprites across the hero. Each entry has separate
// desktop ("lg") and mobile ("sm") positions so they never overlap the title
// or the central CTAs. Mobile positions are kept comfortably away from the
// horizontal edges and well above/below the title block.
const POSITIONS = [
  // top-left
  { lg: { top: "8%",  left: "4%"  }, sm: { top: "8%",  left: "9%"  }, dur: 8.5, dy: 14, rotate: -6 },
  // top-right
  { lg: { top: "16%", right: "5%" }, sm: { top: "8%",  right: "9%" }, dur: 9.5, dy: 12, rotate:  8 },
  // mid-left
  { lg: { top: "44%", left: "2%"  }, sm: { top: "18%", left: "4%"  }, dur: 9.0, dy: 16, rotate:  4 },
  // mid-right
  { lg: { top: "52%", right: "2%" }, sm: { top: "18%", right: "4%" }, dur: 8.0, dy: 18, rotate: -8 },
  // bottom-left
  { lg: { bottom: "8%",  left: "12%" }, sm: { bottom: "10%", left: "8%"  }, dur: 10.0, dy: 12, rotate: 10 },
  // bottom-right
  { lg: { bottom: "12%", right: "14%" }, sm: { bottom: "10%", right: "8%" }, dur: 9.0, dy: 14, rotate: -10 },
  // top-center (above title)
  { lg: { top: "6%",  left: "46%" }, sm: { top: "3%", left: "44%" }, dur: 11.0, dy: 16, rotate: 0 },
];

function FloatingSprite({ src, pos, idx }) {
  const driftControls = useAnimation(); // outer transform (drift on click)
  const floatRef = useRef(null);
  const [drifted, setDrifted] = useState(false);
  const reduceMotion = useReducedMotion();

  // Settle entrance
  useEffect(() => {
    driftControls.start({
      opacity: 1,
      x: 0,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: { duration: 1.0, delay: 0.2 + idx * 0.08, ease: [0.22, 1, 0.36, 1] },
    });
  }, [driftControls, idx]);

  const onPress = useCallback(async () => {
    if (drifted) return;
    setDrifted(true);
    const angle = Math.random() * Math.PI * 2;
    const dist = 240 + Math.random() * 200;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const dr = (Math.random() - 0.5) * 540;
    await driftControls.start({
      x: dx,
      y: dy,
      rotate: dr,
      scale: 0.88,
      transition: { duration: 2.6, ease: [0.16, 1, 0.3, 1] },
    });
    await driftControls.start({
      x: 0,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: { duration: 3.2, ease: [0.34, 1.4, 0.5, 1] },
    });
    setDrifted(false);
  }, [driftControls, drifted]);

  // Inner: continuous floating bob driven by framer-motion sinusoidal animation.
  // This composes with the parent drift transform (we keep them on separate
  // layers — drift on the outer motion.button, bob on the inner motion.span).
  const floatAnim = reduceMotion
    ? { y: 0 }
    : {
        y: [0, -pos.dy, 0, pos.dy * 0.5, 0],
        x: [0, pos.dy * 0.18, 0, -pos.dy * 0.14, 0],
      };

  return (
    <motion.button
      type="button"
      onClick={onPress}
      initial={{ opacity: 0, scale: 0.6, y: 18 }}
      animate={driftControls}
      whileHover={{ scale: 1.12 }}
      whileTap={{ scale: 0.9 }}
      data-testid={`hero-sprite-${idx}`}
      aria-label="Growtopia item"
      className="absolute z-10 pointer-events-auto select-none cursor-pointer p-0 m-0 bg-transparent border-0"
      style={{ ...pos.lg, transformOrigin: "center" }}
    >
      <motion.span
        ref={floatRef}
        className="block will-change-transform"
        animate={floatAnim}
        transition={{
          duration: pos.dur,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
      >
        <span
          className="block sprite-glow"
          style={{ transform: `rotate(${pos.rotate}deg)` }}
        >
          <img
            src={src}
            alt=""
            draggable="false"
            className="block w-9 h-9 sm:w-12 sm:h-12 lg:w-14 lg:h-14 sprite-pixel"
          />
        </span>
      </motion.span>
    </motion.button>
  );
}

export default function HeroFloatingSprites() {
  return (
    <>
      {/* Desktop / tablet positions */}
      <div className="absolute inset-0 hidden sm:block pointer-events-none">
        {SPRITES.map((src, i) => (
          <FloatingSprite key={`d-${i}`} src={src} pos={POSITIONS[i]} idx={i} />
        ))}
      </div>

      {/* Mobile positions — well inside the viewport, away from title */}
      <div className="absolute inset-0 sm:hidden pointer-events-none">
        {SPRITES.map((src, i) => {
          const pos = POSITIONS[i];
          return (
            <FloatingSprite
              key={`m-${i}`}
              src={src}
              pos={{ lg: pos.sm, dur: pos.dur, dy: pos.dy, rotate: pos.rotate }}
              idx={i}
            />
          );
        })}
      </div>
    </>
  );
}
